import type { ZoneProfile } from '../services/zoneService';
import { invoke, isConnectorAvailable, isUnknownTauriCommandError } from './connector';
import { bindingRateCap, hasManualLimitMarker, storedRateCap, type RendererRateCap } from '../utils/rendererRateCap';

const LOCAL_ZONE_IDS = new Set(['this_computer', 'local', 'local_computer', 'this_device']);
const CLEARED = 'none';

const setRendererRateCap = (rendererId: string, maxSampleRate: number | null, maxBitDepth: number | null) =>
  invoke<void>('set_renderer_rate_cap', { rendererId, maxSampleRate, maxBitDepth });

const lastPushed = new Map<string, string>();
let queue: Promise<void> = Promise.resolve();
let connectorLacksCommand = false;

const keyOf = (cap: RendererRateCap | null) => (cap ? `${cap.maxSampleRate}/${cap.maxBitDepth}` : CLEARED);

async function push(zoneId: string, cap: RendererRateCap | null): Promise<void> {
  if (connectorLacksCommand || LOCAL_ZONE_IDS.has(zoneId) || !isConnectorAvailable()) return;
  const key = keyOf(cap);
  if (lastPushed.get(zoneId) === key) return;
  lastPushed.set(zoneId, key);
  try {
    await setRendererRateCap(zoneId, cap?.maxSampleRate ?? null, cap?.maxBitDepth ?? null);
    console.info(`[RateCap] ${zoneId} → ${cap ? `${cap.maxBitDepth}-bit/${cap.maxSampleRate}Hz` : 'cleared'}`);
  } catch (err) {
    lastPushed.delete(zoneId);
    if (isUnknownTauriCommandError(err)) {
      connectorLacksCommand = true;
      return;
    }
    console.warn('[RateCap] set_renderer_rate_cap failed:', err);
  }
}

function enqueue(job: () => Promise<void>): Promise<void> {
  queue = queue.then(job, job);
  return queue;
}

/** App start / profile load: hand every stored limit to the Connector so it holds without re-entering it. */
export function syncStoredRateCaps(profiles: readonly ZoneProfile[]): Promise<void> {
  return enqueue(async () => {
    for (const p of profiles) {
      const cap = storedRateCap(p);
      if (cap) await push(p.zone_id, cap);
    }
  });
}

/**
 * After a Devices edit. A bare rate/depth edit is the manual limit and is sent as typed;
 * test results and resets also rewrite verified state, so the untested default is cleared instead.
 */
export function pushRateCapForUpdate(
  zone: ZoneProfile,
  updates: Partial<ZoneProfile>,
): Promise<void> {
  if (!('max_sample_rate' in updates) && !('max_bit_depth' in updates)) return Promise.resolve();
  // #342: a hand-set Devices limit carries the manual marker in test_results — still the manual limit.
  const manual = hasManualLimitMarker(updates.test_results as Record<string, unknown> | null | undefined);
  const fromTestOrReset = 'verified_at' in updates || ('test_results' in updates && !manual);
  const cap = fromTestOrReset ? storedRateCap(zone) : bindingRateCap(zone);
  return enqueue(() => push(zone.zone_id, cap));
}

export function resetRateCapSyncForTests(): void {
  lastPushed.clear();
  queue = Promise.resolve();
  connectorLacksCommand = false;
}
